import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {debounceTime, distinctUntilChanged, Subscription, take} from "rxjs";
import {AbstractComponent} from "@components";
import {BankData, FetchBankRequest, Lookups, OrganiserPaymentData, TOAST_STATE} from "@model";
import {AuthenticationService, OrganiserPaymentDataService, PaymentService, ToastService} from "@services";
import {required} from "@utility";
import {environment} from "@environment";

@Component({
  selector: 'app-organiser-payment-data-form',
  templateUrl: './organiser-payment-data-form.component.html',
  styleUrls: ['./organiser-payment-data-form.component.scss']
})
export class OrganiserPaymentDataFormComponent extends AbstractComponent {

  @Input() paymentData: OrganiserPaymentData | undefined;
  @Input() organiserId: number | undefined;
  @Input() currencies: Lookups[] = [];
  @Output() paymentDataChanged = new EventEmitter<OrganiserPaymentData>();

  public isTaxDataCollapsed: boolean = false;
  public isBanksDataCollapsed: boolean = false;

  ibanChangeSubscription: Subscription | undefined;
  swiftChangeSubscription: Subscription | undefined;


  inputForm = new FormGroup({
    taxNumber: new FormControl(),
    paysPDV: new FormControl(),
    exemptionText: new FormControl(),
    defaultCurrency: new FormControl(),
    iban: new FormControl(),
    swift: new FormControl(),
    bankName: new FormControl(),
    bankAddress: new FormControl(),
    bankPlace: new FormControl(),
    bankZip: new FormControl(),
  }, {updateOn: 'change'});

  constructor(private organiserPaymentDataService: OrganiserPaymentDataService, private translocoService: TranslocoService,
              private toastService: ToastService, public authenticationService: AuthenticationService, private paymentService: PaymentService) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.initFormValues()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paymentData']?.currentValue !== changes['paymentData']?.previousValue) {
      this.initFormValues();
    }
  }

  initFormValues(): void {
    this.swiftChangeSubscription?.unsubscribe();
    this.ibanChangeSubscription?.unsubscribe();
    if (this.paymentData) {
      this.inputForm.patchValue(this.paymentData);
      if (!(this.paymentData.paysPDV === false)) {
        this.inputForm.controls.exemptionText.disable();
        this.inputForm.controls.paysPDV.setValue(true);
      }
    }
    else {
      this.inputForm.controls.paysPDV.setValue(false);
    }
    this.subscribeToSwiftChange();
    this.subscribeToIbanChange();
  }


  initForm(): void {
    this.subscriptions.push(this.inputForm.valueChanges.pipe(take(1)).subscribe(() => {
      this.inputForm.markAsTouched();
    }));
    this.subscriptions.push(this.inputForm.controls.paysPDV.valueChanges.subscribe((value: boolean) => {
      this.inputForm.controls.exemptionText.clearValidators();
      if (!value) {
        this.inputForm.controls.exemptionText.enable();
        this.inputForm.controls.exemptionText.addValidators([required]);
        this.inputForm.controls.taxNumber.removeValidators([required]);
      } else {
        this.inputForm.controls.exemptionText.setValue(undefined);
        this.inputForm.controls.exemptionText.disable();
        this.inputForm.controls.taxNumber.addValidators([required]);
      }
      this.inputForm.controls.exemptionText.updateValueAndValidity();
      this.inputForm.controls.taxNumber.updateValueAndValidity();
      this.inputForm.markAsTouched();
    }));
    this.inputForm.controls.paysPDV.addValidators([required]);
  }

  private paymentDataFields(): OrganiserPaymentData {
    this.checkValidators(this.inputForm);
    let data: OrganiserPaymentData = {...this.paymentData}
    data.organiserId = this.organiserId;
    data.taxNumber = this.inputForm.value.taxNumber;
    data.paysPDV = this.inputForm.value.paysPDV;
    data.exemptionText = this.inputForm.value.exemptionText;
    data.defaultCurrency = this.inputForm.value.defaultCurrency;
    data.iban = this.inputForm.value.iban;
    data.swift = this.inputForm.value.swift;
    data.bankName = this.inputForm.value.bankName;
    data.bankPlace = this.inputForm.value.bankPlace;
    data.bankZip = this.inputForm.value.bankZip;
    data.bankAddress = this.inputForm.value.bankAddress;
    return data;
  }

  onSave() {
    this.markAllAsTouched(this.inputForm);
    if (!this.inputForm.valid || !this.organiserId || !this.inputForm.touched) {
      return;
    } else {
      let organiserPaymentData: OrganiserPaymentData = this.paymentDataFields();
      if (organiserPaymentData !== undefined) {
        this.organiserPaymentDataService.updatePaymentData(organiserPaymentData, () => {
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("app.common.success.payment.data.edit"))
          this.paymentDataChanged.emit(organiserPaymentData);
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
        });
      }
    }
  }

  private subscribeToIbanChange() {
    this.ibanChangeSubscription = this.inputForm.controls.iban.valueChanges.pipe(debounceTime(environment.fullTextSearchDelay), distinctUntilChanged()).subscribe((iban: string | undefined) => {
      if (!!iban && ((iban.length ?? 0) >= environment.ibanMinLength || iban.trim().length === 0)) {
        this.fetchBankData();
      }
    })
  }

  private subscribeToSwiftChange() {
    this.swiftChangeSubscription = this.inputForm.controls.swift.valueChanges.pipe(debounceTime(environment.fullTextSearchDelay), distinctUntilChanged()).subscribe((swift: string | undefined) => {
      if (!!swift && ((swift.length ?? 0) >= environment.swiftCodeLength || swift.trim().length === 0)) {
        this.fetchBankData();
      }
    });
  }

  private fetchBankData() {
    const iban: string | undefined = this.inputForm.controls.iban.value;
    const swift: string | undefined = this.inputForm.controls.swift.value;
    this.paymentService.fetchBankData(new FetchBankRequest(iban?.trim(), swift?.trim())).subscribe((bankData: BankData | null) => {
      if (bankData?.swift) {
        this.inputForm.controls.swift.setValue(bankData?.swift, {emitEvent: false});
      }
      if (bankData) {
        this.inputForm.controls.bankName.setValue(bankData?.name, {emitEvent: false});
        this.inputForm.controls.bankAddress.setValue(bankData?.address, {emitEvent: false});
        this.inputForm.controls.bankZip.setValue(bankData?.zip, {emitEvent: false});
        this.inputForm.controls.bankPlace.setValue(bankData?.city, {emitEvent: false});
      }
    })
  }

  onCancel() {
    this.inputForm.reset();
  }


  override ngOnDestroy() {
    super.ngOnDestroy();
    this.swiftChangeSubscription?.unsubscribe();
    this.ibanChangeSubscription?.unsubscribe();
  }
}
